import fetchData from "../utils/HttpClient";
import {CustomerInterface} from "../interfaces/customer/CustomerInterface";
import {CustomerManagerInterface} from "../interfaces/customer/CustomerManagerInterface";
import {
    ChatDialogInterface,
    ChatDialogSummaryInterface,
    ChatRating,
    ChatMessageInterface,
    ChatStreamEvent
} from "../interfaces/customer/ChatInterface";

// Клиент — заголовком Authorization (как у остальных запросов кабинета), менеджер клиента — в теле:
// у менеджера свои диалоги
function owner(manager: CustomerManagerInterface | null) {
    return manager ? {manager: {id: manager.id, sign: manager.sign}} : {}
}

export function chatDialogs(customer: CustomerInterface, manager: CustomerManagerInterface | null): Promise<{dialogs: ChatDialogSummaryInterface[]}> {
    return fetchData('/api/b2b/chat/dialogs', owner(manager), 'POST', customer)
}

export function chatDialog(id: string, customer: CustomerInterface, manager: CustomerManagerInterface | null): Promise<ChatDialogInterface> {
    return fetchData(`/api/b2b/chat/dialogs/${id}`, owner(manager), 'POST', customer)
}

export function appendChatDialog(
    dialogId: string | null,
    messages: ChatMessageInterface[],
    customer: CustomerInterface,
    manager: CustomerManagerInterface | null
): Promise<ChatDialogSummaryInterface> {
    return fetchData('/api/b2b/chat/dialogs/append', {
        dialog_id: dialogId,
        messages: messages.map(message => {
            // Модель и время по этапам клиент не передаёт: сервер подставит их по reply_id
            const meta = {
                ...(message.listed ? {products: message.listed} : {}),
                ...(message.replyId ? {reply_id: message.replyId} : {}),
            };
            return {role: message.role, content: message.content, ...(Object.keys(meta).length ? {meta} : {})};
        }),
        ...owner(manager)
    }, 'POST', customer)
}

export function deleteChatDialog(id: string, customer: CustomerInterface, manager: CustomerManagerInterface | null): Promise<{ok: boolean}> {
    return fetchData(`/api/b2b/chat/dialogs/${id}/delete`, owner(manager), 'POST', customer)
}

// Что передано, то и сохраняется: только оценка или только комментарий
export function rateChatDialog(
    id: string,
    change: {rating?: ChatRating, feedback?: {liked: string, disliked: string}},
    customer: CustomerInterface,
    manager: CustomerManagerInterface | null
): Promise<{ok: boolean}> {
    return fetchData(`/api/b2b/chat/dialogs/${id}/rating`, {...change, ...owner(manager)}, 'POST', customer)
}

export function favoriteChatDialog(id: string, favorite: boolean, customer: CustomerInterface, manager: CustomerManagerInterface | null): Promise<{ok: boolean}> {
    return fetchData(`/api/b2b/chat/dialogs/${id}/favorite`, {favorite, ...owner(manager)}, 'POST', customer)
}

/**
 * Ответ консультанта приходит потоком (text/event-stream): события «data: {json}» через пустую строку.
 * Axios в браузере поток не отдаёт по кускам, поэтому здесь fetch.
 */
export async function streamChat(
    messages: ChatMessageInterface[],
    customer: CustomerInterface,
    manager: CustomerManagerInterface | null,
    onEvent: (event: ChatStreamEvent) => void,
    signal: AbortSignal
): Promise<void> {
    const response = await fetch((process.env.REACT_APP_ENDPOINT || '') + '/api/b2b/chat', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Pride ' + customer.id + '-' + customer.sign,
        },
        body: JSON.stringify({
            // Ответ бота — его контекстом (суть для следующих вопросов), если есть; полный текст — только на экране
            messages: messages.map(message => ({role: message.role, content: message.role === 'assistant' && message.context ? message.context : message.content})),
            ...owner(manager)
        }),
        signal,
    });

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        let message = body.message || ('Ошибка ' + response.status);
        if (response.status === 401) {
            message = 'Сессия устарела — выйдите и войдите в личный кабинет заново.';
        } else if (response.status === 429 && response.headers.get('Retry-After')) {
            message += ' Повторите через ' + response.headers.get('Retry-After') + ' с.';
        }
        throw new Error(message);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const {done, value} = await reader.read();
        if (done) {
            return;
        }
        buffer += decoder.decode(value, {stream: true});
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
            const data = part.split('\n')
                .filter(line => line.indexOf('data: ') === 0)
                .map(line => line.slice(6))
                .join('');
            if (data) {
                try {
                    onEvent(JSON.parse(data));
                } catch (e) {
                    // битое событие пропускаем
                }
            }
        }
    }
}
